import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema, buildTechArticleSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is an AI ticketing system?',
    a: 'An AI ticketing system is help desk software where AI performs the clerical work of ticket handling automatically: it reads each incoming message, writes a clean title, picks the category, sets the priority, routes the ticket to the right team, and often drafts the first reply for an agent to approve. Traditional ticketing systems require agents to do this triage manually. Examples in 2026 include Kora, which includes AI ticket structuring at no extra charge, and vendors that sell AI as an add-on.',
  },
  {
    q: 'What is the best AI ticketing system?',
    a: 'The best AI ticketing system depends on pricing model more than feature list. Kora includes free AI ticket structuring and a plain-language AI Analyst (1 credit per answer) — pay-as-you-go credits ($1 = 10 credits, 1 credit per ticket, 1,100 free on signup). Kora does not yet draft replies. Intercom-style per-resolution pricing (around $0.99 per AI resolution) suits large teams that prefer metered costs; Zendesk and Freshdesk sell AI as per-seat add-ons. For small and mid-size teams, included AI is almost always cheaper at real volumes than per-resolution metering.',
  },
  {
    q: 'How much does an AI ticketing system cost?',
    a: 'AI ticketing systems cost between $0 and roughly $115+ per agent per month depending on the model: Kora is pay-as-you-go with credits: $1 buys 10 credits, a ticket costs 1 credit, AI ticket structuring is free, and an AI Analyst answer costs 1 credit — with 1,100 free credits on signup and unlimited members; Freshdesk starts at $19/agent/month plus AI add-ons; Zendesk Suite Team starts at $55/agent/month plus AI add-ons; some vendors add per-resolution AI fees of around $0.99 each. At 1,000 AI resolutions per month, Fin-style metered pricing adds roughly $990/month on top of seats.',
  },
];

const PIPELINE = [
  { step: '1 · Intake', detail: 'A request arrives from email, chat, a web form, or the API, in any format and length. In Kora today you paste it in, or it comes through the API, the support form or the customer portal.' },
  { step: '2 · AI structuring', detail: 'The AI reads the message and generates the title, category, and priority. Kora does this at no extra charge.' },
  { step: '3 · Routing', detail: 'The ticket is assigned to the right person or team based on category and priority, with SLA timers where configured.' },
  { step: '4 · AI reply drafting (Kora AI)', detail: 'Click Draft reply in a ticket and Kora suggests an answer from your knowledge base, helpdesk articles and rules, with sources. You edit and send; nothing goes out automatically. 1 credit per draft.' },
  { step: '5 · Analytics', detail: 'Ask "how many urgent tickets were resolved last month?" in plain language and get the answer without building a report.' },
];

const COMPARISON = [
  { aspect: 'Ticket creation', traditional: 'Agent reads and manually titles, categorizes, prioritizes', aiNative: 'AI generates title, category, and priority automatically' },
  { aspect: 'Routing', traditional: 'Manual assignment or static rules', aiNative: 'AI routes by content, category, and priority' },
  { aspect: 'First reply', traditional: 'Agent writes from scratch', aiNative: 'AI drafts from knowledge base; agent approves' },
  { aspect: 'Reporting', traditional: 'Dashboards and CSV exports', aiNative: 'Plain-language questions answered in seconds' },
  { aspect: 'Pricing', traditional: 'Per agent/month, AI as paid add-on', aiNative: 'Often usage-based (Kora: $1 = 10 credits, 1 credit per ticket, AI structuring free)' },
];

export function AiTicketingSystemPage() {
  useEffect(() => {
    const title = 'AI Ticketing System — How It Works and Costs (2026)';
    const description =
      'What an AI ticketing system is, how the pipeline works from intake to resolution, what it costs across pricing models, and how to evaluate one.';
    setDocumentMeta(title, description);
    setCanonical('/ai-ticketing-system');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/ai-ticketing-system', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'AI Ticketing System', url: 'https://usekora.app/ai-ticketing-system' },
      ]),
      buildTechArticleSchema(title, description, '2026-09-20'),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Guide · Updated September 2026"
      title="AI ticketing system"
      subtitle="How AI ticketing systems work end to end, what they cost, and how they differ from traditional help desks."
      answer={
        <p>
          An AI ticketing system is help desk software that uses AI to handle ticket triage
          automatically: it reads each incoming request, generates a clean title, picks the
          category, sets priority, routes the ticket, and drafts the first reply for agent
          approval. Traditional systems leave this clerical work to agents. Costs vary by model —
          Kora includes free AI ticket structuring and an AI Analyst at 1 credit per answer (pay-as-you-go
          credits: $1 = 10 credits, 1 credit per ticket, 1,100 free on signup); Zendesk and Freshdesk sell AI as per-seat add-ons; some
          vendors meter AI at roughly $0.99 per resolution. At 1,000 resolutions per month,
          metered AI adds about $990/month. For most small and mid-size teams, included AI is
          the cheaper option at real volume.
        </p>
      }
    >
      <ContentSection title="The AI ticketing pipeline">
        <p>
          Every AI ticketing system, whatever the vendor calls the features, runs a version of
          this pipeline:
        </p>
        <div className="space-y-3 not-prose">
          {PIPELINE.map((p) => (
            <div key={p.step} className="rounded-xl border border-zinc-200 p-4">
              <div className="text-sm font-semibold text-zinc-900 mb-1">{p.step}</div>
              <p className="text-sm text-zinc-500 ">{p.detail}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      <ContentSection title="AI-native vs traditional ticketing">
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Aspect</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Traditional</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">AI-native</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((c) => (
                <tr key={c.aspect} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-900 ">{c.aspect}</td>
                  <td className="px-4 py-3 text-zinc-500 ">{c.traditional}</td>
                  <td className="px-4 py-3 text-zinc-600 ">{c.aiNative}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      <ContentSection title="What it costs in practice">
        <p>
          Three pricing models dominate. <strong>Metered per-resolution</strong>: roughly $0.99
          per AI resolution on top of seat pricing — the most expensive model for teams with high
          repetitive volume (1,000 resolutions ≈ $990/month in AI fees alone).{' '}
          <strong>Per-seat AI add-ons</strong>: Zendesk and Freshdesk layer AI onto per-agent
          plans, so costs grow with headcount. <strong>Included in usage pricing</strong>: Kora does not charge extra for AI
          structuring; the AI Analyst costs 1 credit per answer — pay-as-you-go credits ($1 = 10 credits,
          1 credit per ticket, 1,100 free on signup). Your bill scales only with tickets created.
        </p>
        <p>
          For comparisons against specific vendors, see{' '}
          <Link to="/compare/kora-vs-zendesk" className="underline hover:text-zinc-900 ">Kora vs Zendesk</Link>,{' '}
          <Link to="/compare/kora-vs-freshdesk" className="underline hover:text-zinc-900 ">Kora vs Freshdesk</Link>, and{' '}
          <Link to="/compare/kora-vs-intercom" className="underline hover:text-zinc-900 ">Kora vs Intercom</Link>.
        </p>
      </ContentSection>

      <ContentSection title="How to evaluate an AI ticketing system">
        <p>
          Run a two-week test with your real intake, not vendor demos. Measure four things: how
          accurately the AI categorizes your actual tickets, how often its priority calls match
          what an experienced agent would choose, how much edit time a drafted reply saves, and
          what the total monthly bill would be at your real resolution volume. Kora's 1,100 free credits —
          unlimited members, free AI ticket structuring — are enough to run exactly this test.
        </p>
      </ContentSection>

      <ContentSection title="Feed the AI with your own knowledge base">
        <p>
          AI ticketing is only as good as the answers it can draw from. Kora ships with a branded,
          blog-style knowledge base — publish articles at your own link for 1 credit each and they
          become a source Kora AI answers from.{' '}
          <Link to="/knowledge-base-software" className="underline hover:text-zinc-900 ">See how the Helpdesk works →</Link>
        </p>
        <p>
          Kora AI uses that material to draft replies and answer tickets:{' '}
          <Link to="/ai-reply-drafting-software" className="underline hover:text-zinc-900 ">AI reply drafting</Link>,{' '}
          <Link to="/ai-customer-support-agent" className="underline hover:text-zinc-900 ">the AI support agent</Link> and{' '}
          <Link to="/ai-helpdesk-pay-per-resolution" className="underline hover:text-zinc-900 ">pay-per-resolution pricing</Link>.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions people ask about AI ticketing systems." />
    </ContentPage>
  );
}
